import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios'
import applyConverters from 'axios-case-converter'
import axiosRetry from 'axios-retry'
import qs from 'qs'
import { mergeDeepRight } from 'ramda'

import { safeJson } from 'shared/utils/common'
import { ApiError } from 'shared/utils/errors'

import { applySupportSessionConfirmation } from './supportSession'

type FetchFn = <T = unknown>(
  url: string,
  data?: object,
  config?: RequestInit
) => Promise<AxiosResponse<T>>

type FetchGetOrDeleteFn = <T = unknown>(
  url: string,
  config?: RequestInit & { params?: object }
) => Promise<AxiosResponse<T>>

type AxiosGetOrDeleteFn = <T = unknown, C = unknown>(
  url: string,
  config?: object
) => Promise<AxiosResponse<T, C>>

type AxiosFn = <T = unknown, C = unknown>(
  url: string,
  data: object,
  config?: object
) => Promise<AxiosResponse<T, C>>

export type ServerApiService = {
  get: FetchGetOrDeleteFn
  post: FetchFn
  put: FetchFn
  patch: FetchFn
  delete: FetchGetOrDeleteFn
}

export type ClientApiService = {
  get: AxiosGetOrDeleteFn
  post: AxiosFn
  put: AxiosFn
  patch: AxiosFn
  delete: AxiosGetOrDeleteFn
}

export type ApiService = ServerApiService | ClientApiService

const apiUrl = process.env.NEXT_PUBLIC_API_URL || ''

interface ExtendedAxiosRequestError
  extends Omit<AxiosError<unknown>, 'config'> {
  config?: AxiosRequestConfig & {
    prevError?: AxiosError
  } & Partial<InternalAxiosRequestConfig> & {
      headers: AxiosRequestHeaders
    }
}

const applyRetry = (instance: AxiosInstance) => {
  const statusWithoutRetry = [
    401,
    403,
    404,
    422,
    428
  ]
  const isNetworkError = (error?: ExtendedAxiosRequestError) =>
    error?.code === 'ERR_NETWORK'

  axiosRetry(instance, {
    retries: 3,
    onRetry(_, error: ExtendedAxiosRequestError) {
      if (error.config) {
        error.config.prevError = error
      }
    },

    retryDelay: (retryCount: number) => (retryCount + 1) * 350,

    retryCondition: (error: ExtendedAxiosRequestError) => {
      if (isNetworkError(error)) {
        return true
      }

      // intentional stop retry for some requests - exaple createOrder to avoid creating many orders
      if (error.config?.headers?.Noretry) {
        return false
      }

      //if we have 2 different response codes, we need to use the first one - make an order can create order but retur errr payments. Retray will have wrong response code
      if (error.config?.prevError && !isNetworkError(error.config?.prevError)) {
        if (
          error.config?.prevError.response?.status !== error.response?.status
        ) {
          error.response = error.config?.prevError.response

          return false
        }
      }

      if (
        error.response?.status &&
        statusWithoutRetry.includes(error.response?.status)
      ) {
        return false
      }

      return true
    }
  })
}

export const mergeConfig = (config: object) => {
  const defaultConfig = {
    headers: {
      'Access-Control-Allow-Origin': '*'
    }
  }

  return mergeDeepRight(defaultConfig)(config)
}

export const buildUrlWithParams = (url: string, params?: object): string => {
  if (!params || Object.keys(params).length === 0) {
    return url
  }

  const queryString = qs.stringify(params, { arrayFormat: 'brackets' })

  return `${url}?${queryString}`
}

const toCamel = (s: string): string => {
  return s.replace(/([-_][a-z])/gi, $1 => {
    return $1.toUpperCase().replace('-', '').replace('_', '')
  })
}

const convertKeysToCamelCase = <T>(o: unknown): T => {
  if (Array.isArray(o)) {
    return o.map(v => convertKeysToCamelCase(v)) as T
  } else if (o !== undefined && o !== null && o.constructor === Object) {
    return Object.keys(o).reduce((acc, key) => {
      const camelKey = toCamel(key)

      acc[camelKey] = convertKeysToCamelCase<T[keyof T]>(o[key])

      return acc
    }, {} as T)
  }

  return o as T
}

//SERVER API
const fetchGet = async <T = unknown>(
  url: string,
  config?: RequestInit & { params?: object }
): Promise<AxiosResponse<T>> => {
  const params = config?.params
  const fetchConfig = config
    ? (({ params: _params, ...rest }) => rest)(config)
    : {}

  const fullUrl = buildUrlWithParams(`${apiUrl}${url}`, params)

  const response = await fetch(fullUrl, {
    ...(fetchConfig),
    method: 'GET',
    cache: fetchConfig.cache ?? 'default',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...fetchConfig.headers
    },
    next: {
      revalidate: 100
    }
  })

  const responseData = await safeJson<T>(response)
  const camelCaseData = responseData
    ? convertKeysToCamelCase(responseData)
    : undefined

  if (!response.ok) {
    throw new ApiError(url, {
      data: camelCaseData,
      status: response.status,
      statusText: response.statusText
    })
  }

  return {
    data: camelCaseData as T,
    status: response.status,
    statusText: response.statusText,
    headers: {} as AxiosRequestHeaders,
    config: {} as InternalAxiosRequestConfig<unknown>
  }
}

const fetchPost = async <T = unknown>(
  url: string,
  data: object = {},
  config: RequestInit = {}
): Promise<AxiosResponse<T>> => {
  const response = await fetch(`${apiUrl}${url}`, {
    ...config,
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...config.headers
    },
    cache: 'default',
    next: {
      revalidate: 100
    }
  })

  const responseData = await safeJson<T>(response)
  const camelCaseData = responseData
    ? convertKeysToCamelCase(responseData)
    : undefined

  if (!response.ok) {
    throw new ApiError(url, {
      data: camelCaseData,
      status: response.status,
      statusText: response.statusText
    })
  }

  return {
    data: camelCaseData as T,
    status: response.status,
    statusText: response.statusText,
    headers: {} as AxiosRequestHeaders,
    config: {} as InternalAxiosRequestConfig<unknown>
  }
}

const fetchPut = async <T = unknown>(
  url: string,
  data: object = {},
  config: RequestInit = {}
): Promise<AxiosResponse<T>> => {
  const response = await fetch(`${apiUrl}${url}`, {
    ...config,
    method: 'PUT',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...config.headers
    },
    cache: 'default',
    next: {
      revalidate: 100
    }
  })

  const responseData = await safeJson<T>(response)
  const camelCaseData = responseData
    ? convertKeysToCamelCase(responseData)
    : undefined

  if (!response.ok) {
    throw new ApiError(url, {
      data: camelCaseData,
      status: response.status,
      statusText: response.statusText
    })
  }

  return {
    data: camelCaseData as T,
    status: response.status,
    statusText: response.statusText,
    headers: {} as AxiosRequestHeaders,
    config: {} as InternalAxiosRequestConfig<unknown>
  }
}

const fetchPatch = async <T = unknown>(
  url: string,
  data: object = {},
  config: RequestInit = {}
): Promise<AxiosResponse<T>> => {
  const response = await fetch(`${apiUrl}${url}`, {
    ...config,
    method: 'PATCH',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...config.headers
    },
    cache: 'default',
    next: {
      revalidate: 100
    }
  })

  const responseData = await safeJson<T>(response)
  const camelCaseData = responseData
    ? convertKeysToCamelCase(responseData)
    : undefined

  if (!response.ok) {
    throw new ApiError(url, {
      data: camelCaseData,
      status: response.status,
      statusText: response.statusText
    })
  }

  return {
    data: camelCaseData as T,
    status: response.status,
    statusText: response.statusText,
    headers: {} as AxiosRequestHeaders,
    config: {} as InternalAxiosRequestConfig<unknown>
  }
}

const fetchDelete = async <T = unknown>(
  url: string,
  config?: RequestInit & { params?: object }
): Promise<AxiosResponse<T>> => {
  const params = config?.params
   
  const fetchConfig = config
    ? (({ params: _params, ...rest }) => rest)(config)
    : {}
  const fullUrl = buildUrlWithParams(`${apiUrl}${url}`, params)

  const response = await fetch(fullUrl, {
    ...(fetchConfig),
    method: 'DELETE',
    cache: 'default',
    next: {
      revalidate: 100
    }
  })

  const responseData = await safeJson<T>(response)
  const camelCaseData = responseData
    ? convertKeysToCamelCase(responseData)
    : undefined

  if (!response.ok) {
    throw new ApiError(url, {
      data: camelCaseData,
      status: response.status,
      statusText: response.statusText
    })
  }

  return {
    data: camelCaseData as T,
    status: response.status,
    statusText: response.statusText,
    headers: {} as AxiosRequestHeaders,
    config: {} as InternalAxiosRequestConfig<unknown>
  }
}

const serverApiService: ServerApiService = {
  get: fetchGet,
  post: fetchPost,
  put: fetchPut,
  patch: fetchPatch,
  delete: fetchDelete
}

//CLIENT API
const axiosGet =
  <T = unknown>(instance: AxiosInstance) =>
  async (url: string, config: object = {}) => {
    return instance.get<T>(url, mergeConfig(config))
  }

const axiosPost =
  <T = unknown>(instance: AxiosInstance) =>
  async (url: string, data: object = {}, config: object = {}) => {
    return instance.post<T>(url, data, mergeConfig(config))
  }

const axiosPut =
  <T = unknown>(instance: AxiosInstance) =>
  async (url: string, data: object = {}, config: object = {}) => {
    return instance.put<T>(url, data, mergeConfig(config))
  }

const axiosPatch =
  <T = unknown>(instance: AxiosInstance) =>
  async (url: string, data: object = {}, config: object = {}) => {
    return instance.patch<T>(url, data, mergeConfig(config))
  }

const axiosDelete =
  <T = unknown>(instance: AxiosInstance) =>
  async (url: string, config: object = {}) => {
    return instance.delete<T>(url, mergeConfig(config))
  }

const api = (baseURL: string = apiUrl): ClientApiService => {
  if (typeof window === 'undefined') {
    return serverApiService
  }

  // We are on the client, use axios
  const instance = applyConverters(axios.create())

  instance.defaults.baseURL = baseURL
  applyRetry(instance)
  // Raise confirmation dialog for support session requests guarded by 428 status code.
  applySupportSessionConfirmation(instance)

  const clientApiService: ClientApiService = {
    get: axiosGet(instance),
    post: axiosPost(instance),
    put: axiosPut(instance),
    patch: axiosPatch(instance),
    delete: axiosDelete(instance)
  }

  return clientApiService
}

// Add response interceptor for error handling
axios.interceptors.response.use(
  response => response,
  (error: AxiosError) => {
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data
    })

    throw error
  }
)

const internalApiService = () => {
  const instance = axios.create({
    baseURL: '/api'
  })

  applyRetry(instance)

  return instance
}

export const internalApi = internalApiService()

const axiosWithRetryService = (baseURL: string = apiUrl) => {
  const instance = axios.create({
    baseURL
  })

  applyRetry(instance)

  return instance
}

export const axiosWithRetry = axiosWithRetryService()

export default api
